// The eight moon-phase textures, built once per game from the realistic full-moon art
// (farm_bd_moon) with canvas clipping: the shadowed part keeps a faint earthshine, the glow
// ring outside the disc hugs only the lit limb, and the lit part (moonPhase.litPolygon)
// is drawn at full strength through a slightly feathered mask so the terminator is soft.
import { PHASES, illumination, litPolygon, haloPolygon } from './moonPhase.mjs';
import { BD } from './backdropAnchors.mjs';

const SRC = 'farm_bd_moon';
const EARTHSHINE = 0.16;
const FEATHER_PX = 1.2;
const HALO_FEATHER_PX = 6;

export const moonPhaseKey = (phase) => `farm_bd_moon_p${phase}`;

function tracePolygon(ctx, pts) {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
}

// Returns false (and draws nothing) when the moon art has not loaded; true once the eight
// textures exist.
export function ensureMoonPhaseTextures(scene) {
  if (!scene.textures.exists(SRC)) return false;
  if (scene.textures.exists(moonPhaseKey(PHASES - 1))) return true;
  const img = scene.textures.get(SRC).getSourceImage();
  const w = img.width, h = img.height;
  const [fx, fy, fr] = BD[SRC]?.disc ?? [0.5, 0.5, 0.44];
  const cx = fx * w, cy = fy * h, r = fr * w;

  for (let p = 0; p < PHASES; p++) {
    const tex = scene.textures.createCanvas(moonPhaseKey(p), w, h);
    const ctx = tex.getContext();
    const lit = illumination(p);

    // Earthshine: the disc, faintly — only the disc, or the art's own glow would draw a faint
    // outline around the shadowed side.
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.globalAlpha = EARTHSHINE;
    ctx.drawImage(img, 0, 0);
    ctx.restore();

    // The glow ring (everything outside the disc), only around the LIT limb: the lit shape
    // with its limb pushed out and its terminator kept on the disc, intersected with the
    // outside of the disc. A full ring also outlined the shadowed half, which a real moon
    // does not do.
    const halo = haloPolygon(p, r, cx, cy);
    if (halo.length) {
      // Through a strongly feathered mask, so the glow fades out where the lit edge meets the
      // shadow instead of stopping in a straight line above and below the moon.
      const layer = document.createElement('canvas');
      layer.width = w; layer.height = h;
      const l = layer.getContext('2d');
      l.filter = `blur(${HALO_FEATHER_PX}px)`;
      l.fillStyle = '#fff';
      tracePolygon(l, halo);
      l.fill();
      l.filter = 'none';
      l.globalCompositeOperation = 'source-in';
      l.drawImage(img, 0, 0);
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.arc(cx, cy, r, 0, Math.PI * 2, true);
      ctx.clip('evenodd');          // outside the disc only: the disc itself is drawn below
      ctx.globalAlpha = 0.4 + 0.6 * lit;
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // The lit part through a feathered mask (a blurred polygon, then the moon "source-in").
    const pts = litPolygon(p, r + 0.5, cx, cy);
    if (pts.length) {
      const layer = document.createElement('canvas');
      layer.width = w; layer.height = h;
      const l = layer.getContext('2d');
      l.filter = `blur(${FEATHER_PX}px)`;   // ignored where unsupported: a hard edge then
      l.fillStyle = '#fff';
      tracePolygon(l, pts);
      l.fill();
      l.filter = 'none';
      l.globalCompositeOperation = 'source-in';
      l.drawImage(img, 0, 0);
      ctx.drawImage(layer, 0, 0);
    }
    tex.refresh();
  }
  return true;
}
